/**
 * The Reports menu for one area (Strategy, Priorities, Plans, Action): All
 * Reports, then the platform reports placed in that area (REPORT_REGISTRY, or
 * the workspace's own placement from Settings → Reports), then Custom Reports.
 * Both sidebars draw it, so a report placed in an area appears in that area's
 * menu wherever the menu is drawn.
 */

import { useEffect, useMemo } from 'react';
import { Activity, BarChart3, FileText, Filter, LineChart, ListOrdered, RefreshCcw, Target, Timer, Workflow } from 'lucide-react';
import type { AppArea } from '../../lib/appAreas';
import type { AreaMenuItem } from '../../lib/areaMenus';
import { useModuleGateResolver } from '../../lib/moduleGate';
import { reportsForArea } from '../../lib/reportSettingsService';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { useReportSettingsStore } from '../../stores/prioritization/reportSettingsStore';

const REPORT_ICON: Record<string, AreaMenuItem['icon']> = {
  'pipeline-health': LineChart,
  'session-reports': ListOrdered,
  'cycle-execution': RefreshCcw,
  'decision-latency': Timer,
  'cycle-reports': Workflow,
  'relationship-health': Activity,
  'intake-clarity': Filter,
  'strategy-alignment': Target,
};

export function useAreaReports(area: AppArea): AreaMenuItem[] {
  return useReportsMenu([area]);
}

const ALL_AREAS: readonly AppArea[] = ['strategy', 'priorities', 'plans', 'action'];

/**
 * The Reports menu with the reports placed in any area — for a frame that
 * lists every area's pages at once (the phone's More drawer), so a report
 * placed in one area is not lost to someone who never switches to it.
 */
export function useAllAreaReports(): AreaMenuItem[] {
  return useReportsMenu(ALL_AREAS);
}

function useReportsMenu(areas: readonly AppArea[]): AreaMenuItem[] {
  const workspaceId = getCurrentWorkspaceId();
  const settings = useReportSettingsStore((s) => s.settings);
  const fetchSettings = useReportSettingsStore((s) => s.fetchSettings);
  const isModuleOn = useModuleGateResolver();

  useEffect(() => {
    if (workspaceId) void fetchSettings(workspaceId);
  }, [workspaceId, fetchSettings]);

  const placed = areas
    .flatMap((a) => reportsForArea(a, settings, isModuleOn))
    .filter((r, i, all) => all.findIndex((o) => o.key === r.key) === i);
  const placedKeys = placed.map((r) => r.key).join(',');

  return useMemo(
    () => [
      { name: 'All Reports', href: '/prioritization/reports', icon: BarChart3, match: 'exact' },
      ...placed.map((r) => ({
        name: r.name,
        href: r.href,
        icon: REPORT_ICON[r.key] ?? BarChart3,
        match: 'prefix' as const,
        isSubItem: true,
      })),
      { name: 'Custom Reports', href: '/prioritization/reports/custom', icon: FileText, match: 'prefix' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placedKeys],
  );
}
